import { WhatsAppTemplate } from '../services/api.ts';

export type AutomationFlowType = 'follow_up' | 'confirmation';

export interface TemplateVariableOption {
  key: string;
  label: string;
  description: string;
  flows: AutomationFlowType[];
}

export interface TemplateMessageParameterConfig {
  type: 'text';
  source: string;
  value?: string;
}

export interface TemplateMessageComponentConfig {
  type: 'body' | 'header';
  parameters: TemplateMessageParameterConfig[];
}

export interface TemplateMessageConfig {
  template_name: string;
  language: string;
  components: TemplateMessageComponentConfig[];
}

export interface TemplateVariableSlot {
  id: string;
  componentType: 'BODY' | 'HEADER';
  parameterIndex: number;
  placeholder: string;
}

export interface TemplateSupportAnalysis {
  bodyText: string;
  slots: TemplateVariableSlot[];
  unsupportedReasons: string[];
}

const PLACEHOLDER_REGEX = /{{\s*(\d+)\s*}}/g;

const TEMPLATE_VARIABLE_OPTIONS: TemplateVariableOption[] = [
  {
    key: 'contact_first_name',
    label: 'Primeiro nome',
    description: 'Primeiro nome do contato/lead/paciente',
    flows: ['follow_up', 'confirmation'],
  },
  {
    key: 'contact_full_name',
    label: 'Nome completo',
    description: 'Nome completo do contato/lead/paciente',
    flows: ['follow_up', 'confirmation'],
  },
  {
    key: 'current_date',
    label: 'Data atual',
    description: 'Data atual no fuso da clínica',
    flows: ['follow_up', 'confirmation'],
  },
  {
    key: 'current_time',
    label: 'Hora atual',
    description: 'Hora atual no fuso da clínica',
    flows: ['follow_up', 'confirmation'],
  },
  {
    key: 'assistant_name',
    label: 'Nome da assistente',
    description: 'Nome da assistente usado nas mensagens',
    flows: ['follow_up', 'confirmation'],
  },
  {
    key: 'assistant_role',
    label: 'Cargo da assistente',
    description: 'Cargo ou função da assistente',
    flows: ['follow_up', 'confirmation'],
  },
  {
    key: 'assistant_responsibility',
    label: 'Responsabilidade da assistente',
    description: 'Responsabilidade da assistente no atendimento',
    flows: ['follow_up', 'confirmation'],
  },
  {
    key: 'clinic_name',
    label: 'Nome da clínica',
    description: 'Nome cadastrado da clínica',
    flows: ['follow_up', 'confirmation'],
  },
  {
    key: 'clinic_location',
    label: 'Bairro / região',
    description: 'Bairro ou região da clínica',
    flows: ['follow_up', 'confirmation'],
  },
  {
    key: 'clinic_address',
    label: 'Endereço da clínica',
    description: 'Endereço completo cadastrado da clínica',
    flows: ['follow_up', 'confirmation'],
  },
  {
    key: 'clinic_whatsapp',
    label: 'WhatsApp da clínica',
    description: 'WhatsApp cadastrado da clínica',
    flows: ['follow_up', 'confirmation'],
  },
  {
    key: 'clinic_phone',
    label: 'Telefone fixo',
    description: 'Telefone fixo cadastrado da clínica',
    flows: ['follow_up', 'confirmation'],
  },
  {
    key: 'clinic_maps',
    label: 'Link do Google Maps',
    description: 'Link do Google Maps da clínica',
    flows: ['follow_up', 'confirmation'],
  },
  {
    key: 'clinic_instagram',
    label: 'Instagram',
    description: 'Instagram cadastrado da clínica',
    flows: ['follow_up', 'confirmation'],
  },
  {
    key: 'clinic_facebook',
    label: 'Facebook',
    description: 'Facebook cadastrado da clínica',
    flows: ['follow_up', 'confirmation'],
  },
  {
    key: 'clinic_site',
    label: 'Site da clínica',
    description: 'Site cadastrado da clínica',
    flows: ['follow_up', 'confirmation'],
  },
  {
    key: 'clinic_history',
    label: 'História da clínica',
    description: 'História ou apresentação da clínica',
    flows: ['follow_up', 'confirmation'],
  },
  {
    key: 'technical_responsible',
    label: 'Responsável técnico',
    description: 'Responsável técnico cadastrado',
    flows: ['follow_up', 'confirmation'],
  },
  {
    key: 'evaluation_price',
    label: 'Preço da avaliação',
    description: 'Valor da avaliação cadastrado',
    flows: ['follow_up', 'confirmation'],
  },
  {
    key: 'treatment_prices',
    label: 'Preços de tratamentos',
    description: 'Preços dos tratamentos cadastrados',
    flows: ['follow_up', 'confirmation'],
  },
  {
    key: 'payment_methods',
    label: 'Formas de pagamento',
    description: 'Formas de pagamento aceitas',
    flows: ['follow_up', 'confirmation'],
  },
  {
    key: 'installment_conditions',
    label: 'Condições de parcelamento',
    description: 'Condições de parcelamento cadastradas',
    flows: ['follow_up', 'confirmation'],
  },
  {
    key: 'health_insurance_plans',
    label: 'Convênios aceitos',
    description: 'Convênios aceitos pela clínica',
    flows: ['follow_up', 'confirmation'],
  },
  {
    key: 'appointment_date',
    label: 'Data do agendamento',
    description: 'Data do agendamento',
    flows: ['follow_up', 'confirmation'],
  },
  {
    key: 'appointment_time',
    label: 'Hora do agendamento',
    description: 'Horário do agendamento',
    flows: ['follow_up', 'confirmation'],
  },
  {
    key: 'appointment_datetime',
    label: 'Data e hora do agendamento',
    description: 'Data/hora completa do agendamento',
    flows: ['follow_up', 'confirmation'],
  },
  {
    key: 'appointment_protocol',
    label: 'Protocolo',
    description: 'Protocolo derivado do agendamento',
    flows: ['follow_up', 'confirmation'],
  },
  {
    key: 'appointment_interest',
    label: 'Interesse/Tratamento',
    description: 'Interesse registrado no agendamento',
    flows: ['follow_up', 'confirmation'],
  },
  {
    key: 'literal',
    label: 'Texto fixo',
    description: 'Texto digitado manualmente para este parâmetro',
    flows: ['follow_up', 'confirmation'],
  },
];

const EXAMPLE_VALUES_BY_USE_CASE: Record<string, string[]> = {
  follow_up: ['Joao', 'avaliacao de implante', 'amanha'],
  confirmation: ['Joao', '25/03/2026', '14:30', 'AG001234'],
  noshow: ['Joao', '25/03/2026'],
  pos_consulta: ['Joao', 'limpeza'],
  pos_venda: ['Joao', 'implante'],
  campaign: ['Joao', 'avaliacao odontologica'],
  chat_active: ['Joao', 'sua avaliacao'],
  general: ['Joao', 'informacao'],
};

function getPlaceholders(text: string): number[] {
  const placeholders = new Set<number>();
  for (const match of text.matchAll(PLACEHOLDER_REGEX)) {
    const numericValue = Number(match[1]);
    if (!Number.isNaN(numericValue)) {
      placeholders.add(numericValue);
    }
  }
  return Array.from(placeholders).sort((left, right) => left - right);
}

function getDefaultSource(flowType: AutomationFlowType, parameterIndex: number): string {
  if (flowType === 'confirmation') {
    if (parameterIndex === 0) return 'contact_first_name';
    if (parameterIndex === 1) return 'appointment_date';
    if (parameterIndex === 2) return 'appointment_time';
    if (parameterIndex === 3) return 'appointment_protocol';
  }

  if (parameterIndex === 0) {
    return 'contact_first_name';
  }

  return 'literal';
}

export function getTemplateVariableOptions(flowType: AutomationFlowType): TemplateVariableOption[] {
  return TEMPLATE_VARIABLE_OPTIONS.filter((option) => option.flows.includes(flowType));
}

export function getTemplateVariableOption(
  source: string,
  flowType: AutomationFlowType,
): TemplateVariableOption | undefined {
  return getTemplateVariableOptions(flowType).find((option) => option.key === source);
}

export function analyzeTemplateSupport(template: WhatsAppTemplate): TemplateSupportAnalysis {
  const slots: TemplateVariableSlot[] = [];
  const unsupportedReasons: string[] = [];
  let bodyText = '';

  for (const component of template.components || []) {
    if (!component || typeof component !== 'object') {
      continue;
    }

    const componentType = String(component.type || '').toUpperCase();

    if (componentType === 'BODY') {
      bodyText = typeof component.text === 'string' ? component.text : bodyText;
      if (typeof component.text === 'string') {
        const placeholders = getPlaceholders(component.text);
        placeholders.forEach((placeholder, index) => {
          slots.push({
            id: `BODY-${index}`,
            componentType: 'BODY',
            parameterIndex: index,
            placeholder: `{{${placeholder}}}`,
          });
        });
      }
      continue;
    }

    if (componentType === 'HEADER') {
      const headerFormat = String(component.format || '').toUpperCase();
      if (headerFormat && headerFormat !== 'TEXT') {
        unsupportedReasons.push('HEADER de mídia ainda não é suportado na automação.');
        continue;
      }

      if (typeof component.text === 'string') {
        const placeholders = getPlaceholders(component.text);
        placeholders.forEach((placeholder, index) => {
          slots.push({
            id: `HEADER-${index}`,
            componentType: 'HEADER',
            parameterIndex: index,
            placeholder: `{{${placeholder}}}`,
          });
        });
      }
      continue;
    }

    if (componentType === 'BUTTONS') {
      const buttons = Array.isArray(component.buttons) ? component.buttons : [];
      const hasDynamicButton = buttons.some((button) => {
        if (!button || typeof button !== 'object') {
          return false;
        }
        return (
          (typeof button.url === 'string' && button.url.includes('{{')) ||
          (typeof button.text === 'string' && button.text.includes('{{'))
        );
      });
      if (hasDynamicButton) {
        unsupportedReasons.push('Botões com parâmetro dinâmico ainda não são suportados na automação.');
      }
    }
  }

  return {
    bodyText,
    slots,
    unsupportedReasons,
  };
}

export function isTemplateSupportedForAutomation(template: WhatsAppTemplate): boolean {
  return analyzeTemplateSupport(template).unsupportedReasons.length === 0;
}

export function parseStoredTemplateMessage(
  rawContent: string | TemplateMessageConfig | null | undefined,
): TemplateMessageConfig | null {
  if (!rawContent) {
    return null;
  }

  if (typeof rawContent === 'object' && 'template_name' in rawContent) {
    return rawContent;
  }

  if (typeof rawContent !== 'string') {
    return null;
  }

  const trimmedContent = rawContent.trim();
  if (!trimmedContent) {
    return null;
  }

  if (trimmedContent.startsWith('{')) {
    try {
      const parsedContent = JSON.parse(trimmedContent);
      if (parsedContent && typeof parsedContent === 'object' && parsedContent.template_name) {
        return parsedContent as TemplateMessageConfig;
      }
    } catch (error) {
      console.warn('Nao foi possivel parsear template salvo, usando fallback simples.', error);
    }
  }

  return {
    template_name: trimmedContent,
    language: 'pt_BR',
    components: [],
  };
}

export function buildTemplateMessageConfig(
  template: WhatsAppTemplate,
  flowType: AutomationFlowType,
  existingConfig?: TemplateMessageConfig | null,
): TemplateMessageConfig {
  const analysis = analyzeTemplateSupport(template);
  const existingComponents = existingConfig?.components || [];

  const componentTypes = Array.from(new Set(analysis.slots.map((slot) => slot.componentType)));

  const components = componentTypes.map((componentType) => {
    const existingComponent = existingComponents.find(
      (component) => component.type.toUpperCase() === componentType,
    );

    const slotsForComponent = analysis.slots.filter((slot) => slot.componentType === componentType);

    return {
      type: componentType.toLowerCase() as 'body' | 'header',
      parameters: slotsForComponent.map((slot) => {
        const existingParameter = existingComponent?.parameters?.[slot.parameterIndex];
        if (existingParameter?.source) {
          return existingParameter;
        }

        const defaultSource = getDefaultSource(flowType, slot.parameterIndex);
        return {
          type: 'text' as const,
          source: defaultSource,
          value: defaultSource === 'literal' ? '' : undefined,
        };
      }),
    };
  });

  return {
    template_name: template.name,
    language: template.language || existingConfig?.language || 'pt_BR',
    components,
  };
}

export function serializeTemplateMessage(config: TemplateMessageConfig): string {
  return JSON.stringify(config);
}

export function getTemplateDisplayName(
  content: string | TemplateMessageConfig | null | undefined,
): string {
  const parsedContent = parseStoredTemplateMessage(content);
  return parsedContent?.template_name || '';
}

export function getTemplateParameterSummary(
  config: TemplateMessageConfig | null | undefined,
  flowType: AutomationFlowType,
): string[] {
  if (!config) {
    return [];
  }

  return (config.components || []).flatMap((component) =>
    (component.parameters || []).map((parameter, index) => {
      if (parameter.source === 'literal') {
        return `Parametro ${index + 1}: ${parameter.value || 'Texto fixo vazio'}`;
      }

      return `Parametro ${index + 1}: ${
        getTemplateVariableOption(parameter.source, flowType)?.label || parameter.source
      }`;
    }),
  );
}

export function getTemplateExampleValues(useCase: string, variableCount: number): string[] {
  const seedValues = EXAMPLE_VALUES_BY_USE_CASE[useCase] || EXAMPLE_VALUES_BY_USE_CASE.general;
  const examples = [...seedValues];

  while (examples.length < variableCount) {
    examples.push(`Exemplo ${examples.length + 1}`);
  }

  return examples.slice(0, variableCount);
}

export function countBodyVariables(text: string): number {
  return getPlaceholders(text).length;
}
